import { isDefined } from '@sgme/fp';
import clsx from 'clsx';
import type { ChangeEvent, InputHTMLAttributes } from 'react';

import { useComponentId } from '@/components/sg-design-system/types/components.ts';
import type { InputState } from '@/components/sg-design-system/types/input.ts';

import type { Size } from '../../../types/layout';

export interface SgCheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;

  size?: Size;
  outline?: boolean;

  validationState?: InputState;

  className?: string;
  dataE2e?: string;

  onCheckBoxChange?: (isChecked: boolean) => void;
}

/**
 * Icon with the attributes found in Sigma design.
 *
 * @example
 * <Checkbox size="lg" checked={true} label="is OK ?" />
 */
export const SgCheckbox = (props: SgCheckboxProps) => {
  const {
    label = '',
    size = 'md',
    outline: isOutline = false,
    validationState,
    className,
    dataE2e,
    onCheckBoxChange,
    ...otherProps
  } = props;

  const checkboxId = useComponentId();

  const onChange =
    otherProps.onChange ??
    ((event: ChangeEvent<HTMLInputElement>) => isDefined(onCheckBoxChange) && onCheckBoxChange(event.target.checked));

  const checkboxClassName = clsx(['form-check my-0', `form-check-${size}`, className], {
    'form-check-edited': validationState === 'edited',
    'form-check-valid': validationState === 'valid',
    'form-check-warning': validationState === 'warning',
    'form-check-invalid': validationState === 'invalid',

    'form-control-outline': isOutline,
  });

  return (
    <div className={checkboxClassName}>
      {/* label can be empty */}
      <input
        type="checkbox"
        id={checkboxId}
        className="form-check-input"
        data-e2e={dataE2e}
        onChange={onChange}
        {...otherProps}
      />
      <label
        className="form-check-label"
        htmlFor={checkboxId}
        data-e2e={isDefined(dataE2e) ? `${dataE2e}.label` : undefined}
      >
        {label}
      </label>
    </div>
  );
};
